import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useGetAnalysisList } from '../../api/generated/insights';
import RouteGuardLoading from './RouteGuardLoading';

/**
 * Route guard that validates analysisId parameter exists in the brand's analysis list.
 * Redirects to /brands/:brandId if analysisId is invalid or doesn't exist.
 * Assumes brandId has already been validated by BrandIdGuard.
 * Renders nested routes via Outlet.
 */
export const AnalysisIdGuard: React.FC = () => {
  const { brandId, analysisId } = useParams<{
    brandId: string;
    analysisId: string;
  }>();

  const {
    data: analyses,
    isLoading,
    error,
  } = useGetAnalysisList(brandId!, {
    query: {
      enabled: !!brandId, // Only fetch if brandId exists
      select: ({ data }) => data?.analyses || [],
    },
  });

  // Show loading state while fetching analyses
  if (isLoading) {
    return <RouteGuardLoading />;
  }

  // If there's an error fetching analyses, redirect to brand page
  if (error) {
    console.error('Error fetching analyses for validation:', error);
    return <Navigate to={`/brands/${brandId}`} replace />;
  }

  // If analysisId is missing or invalid, redirect to brand page
  if (!analysisId || analysisId.trim() === '') {
    return <Navigate to={`/brands/${brandId}`} replace />;
  }

  // Handle special case: "latest" is always valid
  if (analysisId === 'latest') {
    return <Outlet />;
  }

  // Check if analysisId exists in the brand's analysis list
  const analysisExists = analyses?.some(
    (analysis) => analysis.id === analysisId,
  );

  if (!analysisExists) {
    return <Navigate to={`/brands/${brandId}`} replace />;
  }

  // analysisId is valid, render nested routes
  return <Outlet />;
};

export default AnalysisIdGuard;
